import {
  useInfiniteQuery,
  QueryKey,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query'
import { Paginated } from '../types/IPaginationResponse'

type InfiniteResult<T> = {
  items: T[]
  meta: Paginated<T>['meta']
}

interface InfiniteOptions<TParams, TItem>
  extends Omit<
    UseInfiniteQueryOptions<
      Paginated<TItem>,
      Error,
      InfiniteResult<TItem>
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  > {
  queryKey: QueryKey
  fetchFn: (
    params: TParams & { page: number }
  ) => Promise<Paginated<TItem>>
  params: TParams
  initialPage?: number
}

export function useInfinitePaginated<
  TParams extends object,
  TItem
>({
  queryKey,
  fetchFn,
  params,
  initialPage = 1,
  ...options
}: InfiniteOptions<TParams, TItem>) {
  return useInfiniteQuery<
    Paginated<TItem>,
    Error,
    InfiniteResult<TItem>
  >({
    queryKey,

    initialPageParam: initialPage,

    queryFn: ({ pageParam }) =>
      fetchFn({
        ...params,
        page: pageParam as number,
      }),

    getNextPageParam: (lastPage) => {
      const { page, limit, total } = lastPage.meta
      return page * limit < total
        ? page + 1
        : undefined
    },

    select: (data) => ({
      items: data.pages.flatMap((p) => p.items),
      meta: data.pages.at(-1)!.meta,
    }),

    ...options, // 🔥 критично важливо
  })
}
